import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Loader from "../../components/common/Loader";
import applicationStatusService from "../../services/applicationStatusService";
import { FILE_BASE_URL } from "../../services/api";
import { formatDate } from "../../utils/format";

const STATUS_LABEL = {
  pending: "Pending",
  in_review: "In review",
  shortlisted: "Shortlisted",
  waitlisted: "Waitlisted",
  rejected: "Not selected",
};

function displayValue(value) {
  return value === null || value === undefined || value === ""
    ? "—"
    : value;
}

export default function PanelStudentDetail() {
  const { roundId, studentId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");
    setData(null);

    applicationStatusService
      .panelStudentDetail(roundId, studentId)
      .then((response) => {
        if (cancelled) return;

        if (!response.success || !response.data) {
          throw new Error("Could not load student details.");
        }

        setData(response.data);
      })
      .catch((err) => {
        if (cancelled) return;

        setError(
          err.response?.data?.message ||
            err.friendlyMessage ||
            err.message ||
            "Could not load student details."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roundId, studentId, retryCount]);

  const backLink = (
    <Link
      to={`/panel/rounds/${roundId}`}
      className="pm-back-link"
    >
      ← Back to candidates
    </Link>
  );

  if (loading) {
    return (
      <div className="pm-page">
        {backLink}
        <Loader label="Loading student details" />
      </div>
    );
  }

  if (error || !data) {
      return (
    <div className="pm-page pm-panel-student-detail">
      {backLink}

        <div className="pm-card" role="alert">
          <h2>Unable to open student details</h2>
          <p>{error || "Student details are unavailable."}</p>

          <button
            type="button"
            className="pm-btn pm-btn--primary pm-btn--sm"
            onClick={() => setRetryCount((count) => count + 1)}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-page">
      {backLink}

      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">
            {data.university_name} · Panel review
          </span>
          <h1>{data.name}</h1>
          <p className="pm-body-text">
            Reviewing: {data.reviewing_round_name}
          </p>
        </div>
      </header>

      <div className="pm-detail-grid">
        <div className="pm-card">
          <h3>Student details</h3>

          <dl className="pm-dl">
            <div>
              <dt>Name</dt>
              <dd>{displayValue(data.name)}</dd>
            </div>
            <div>
              <dt>Student ID</dt>
              <dd>{displayValue(data.student_id)}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd style={{ overflowWrap: "anywhere" }}>
                {displayValue(data.email_id)}
              </dd>
            </div>
            <div>
              <dt>Specialisation</dt>
              <dd>{displayValue(data.specialisation)}</dd>
            </div>
          </dl>
        </div>

        <div className="pm-card">
          <h3>Application overview</h3>

          <dl className="pm-dl">
            <div>
              <dt>University</dt>
              <dd>{displayValue(data.university_name)}</dd>
            </div>
            <div>
              <dt>Applied</dt>
              <dd>
                {data.applied_date
                  ? formatDate(data.applied_date)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{displayValue(data.location)}</dd>
            </div>
            <div>
              <dt>Hiring type</dt>
              <dd>{displayValue(data.type_of_hire)}</dd>
            </div>
          </dl>
        </div>

        <div className="pm-card">
          <h3>Academics</h3>

          <dl className="pm-dl">
            <div>
              <dt>10th score</dt>
              <dd>{displayValue(data.tenth_score)}</dd>
            </div>
            <div>
              <dt>10th passing year</dt>
              <dd>{data.tenth_pass_year || "Not provided"}</dd>
            </div>

            <div>
              <dt>12th score</dt>
              <dd>{displayValue(data.twelfth_score)}</dd>
            </div>
            <div>
              <dt>12th passing year</dt>
              <dd>{data.twelfth_pass_year || "Not provided"}</dd>
            </div>

            <div>
              <dt>Undergraduate CGPA</dt>
              <dd>{displayValue(data.undergrad_cgpa)}</dd>
            </div>
            <div>
              <dt>Undergraduate passing year</dt>
              <dd>{data.undergrad_pass_year || "Not provided"}</dd>
            </div>

            <div>
              <dt>Current CGPA</dt>
              <dd>{displayValue(data.current_cgpa)}</dd>
            </div>
            <div>
              <dt>Application passing year</dt>
              <dd>{data.education_pass_yr || "Not provided"}</dd>
            </div>
          </dl>
        </div>

                <div className="pm-card">
          <h3>Full-time work experience</h3>

          {data.has_work_experience === "yes" ? (
            <dl className="pm-dl">
              <div>
                <dt>Has work experience</dt>
                <dd>Yes</dd>
              </div>

              <div>
                <dt>Total years</dt>
                <dd>{displayValue(data.work_experience_years)}</dd>
              </div>

              <div className="pm-panel-detail__organizations">
                <dt>Organizations / details provided</dt>
                <dd>
                  {data.work_experience_organizations ||
                    "Not provided"}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="pm-body-text">
              {data.has_work_experience === "no"
                ? "No full-time work experience."
                : "Work experience information was not provided."}
            </p>
          )}
        </div>

        <div className="pm-card">
          <h3>Documents</h3>

          <div className="pm-doc-list">
            {data.cv ? (
              <a
                className="pm-doc-link"
                href={`${FILE_BASE_URL}/${data.cv}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                📄 Open CV / Resume
              </a>
            ) : (
              <p className="pm-body-text">No CV uploaded.</p>
            )}

            {data.video ? (
              <a
                className="pm-doc-link"
                href={`${FILE_BASE_URL}/${data.video}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                🎬 Open introduction video
                {data.video_scan_status === "pending"
                  ? " (verifying…)"
                  : ""}
              </a>
            ) : (
              <p className="pm-body-text">No video uploaded.</p>
            )}
          </div>
        </div>
      </div>

      <div className="pm-card" style={{ marginTop: 20 }}>
        <h3>This round: {data.reviewing_round_name}</h3>

        <span
          className={`pm-status-pill pm-status-pill--${data.reviewing_round_status}`}
        >
          {STATUS_LABEL[data.reviewing_round_status] ||
            data.reviewing_round_status}
        </span>

        <p
          className="pm-body-text"
          style={{ marginTop: 14, whiteSpace: "pre-wrap" }}
        >
          {data.reviewing_round_feedback ||
            "No feedback has been saved for this round."}
        </p>

        <Link
          to={`/panel/rounds/${roundId}`}
          className="pm-btn pm-btn--outline pm-btn--sm"
        >
          Back to round to add feedback
        </Link>
      </div>

      <div className="pm-card" style={{ marginTop: 20 }}>
        <h3>Application progress</h3>

        {!data.timeline?.length ? (
          <p className="pm-body-text">
            No active rounds are available.
          </p>
        ) : (
          <div className="pm-round-timeline">
            {data.timeline.map((step) => (
              <div
                key={step.round_id}
                className={`pm-round-step pm-round-step--${
                  step.status || "upcoming"
                }`}
              >
                <span className="pm-round-step__dot" />

                <div>
                  <strong>{step.round_name}</strong>
                  <span>
                    {STATUS_LABEL[step.status] ||
                      step.status ||
                      "Upcoming"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}